import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi, LoginRequest, RegisterRequest } from '@/services/api/auth';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/services/supabase';

export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { login: storeLogin, logout: storeLogout, user, isAuthenticated } = useAuthStore();

  const loginMutation = useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (data) => {
      storeLogin(data.user, data.token);
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      navigate('/dashboard');
    },
  });

  const registerMutation = useMutation({
    mutationFn: (data: RegisterRequest) => authApi.register(data),
    onSuccess: () => {
      navigate('/login');
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Sign out from Supabase first (clears local session)
      await supabase.auth.signOut();
      // Backend logout is best-effort (session may already be invalid)
      try {
        await authApi.logout();
      } catch {
        // Ignore backend errors - local session is already cleared
      }
    },
    onSuccess: () => {
      storeLogout();
      queryClient.clear();
      navigate('/login');
    },
    onError: () => {
      // Even if something fails, force-clear local state and redirect
      storeLogout();
      queryClient.clear();
      navigate('/login');
    },
  });

  const { data: currentUser, isLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => authApi.getCurrentUser(),
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return {
    user: currentUser || user,
    isAuthenticated,
    isLoading,
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout: logoutMutation.mutate,
    isLoginLoading: loginMutation.isPending,
    isRegisterLoading: registerMutation.isPending,
    loginError: loginMutation.error,
    registerError: registerMutation.error,
  };
};
